import { ArchiveX, CircleSlash, Layers, OctagonAlert, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import { cn } from '~/utils/cn';
import { formatDate } from '~/utils/date';

import type { DeliveryRunDetail, ProductsUnavailableReason } from '../types/delivery-runs.types';
import { describeDeliveryError } from '../utils/delivery-error-copy';
import { canRetryDelivery } from '../utils/derive-delivery-actions';
import { humanizeEnum } from '../utils/format-enum';
import type { ResolvedProductsUnavailable } from '../utils/products-unavailable';

import { RetryDeliveryButton } from './retry-delivery-button';

/** Per-reason icon, icon-badge tone and copy-key stem under `detail.products.unavailable`. */
const REASON_STYLE: Record<
  ProductsUnavailableReason,
  { icon: LucideIcon; badge: string; key: string }
> = {
  FAILED_BEFORE_PRODUCTS: {
    icon: OctagonAlert,
    badge: 'bg-error-50 text-error',
    key: 'failedBeforeProducts',
  },
  RETENTION_EXPIRED: { icon: ArchiveX, badge: 'bg-info-50 text-info', key: 'retentionExpired' },
  NOT_TRACKED: { icon: CircleSlash, badge: 'bg-muted text-muted-foreground', key: 'notTracked' },
  ALL_SUPERSEDED: { icon: Layers, badge: 'bg-info-50 text-info', key: 'allSuperseded' },
};

interface Props {
  info: ResolvedProductsUnavailable;
  /** The run, when the host has it — names the delivery type and enables Retry delivery. */
  run?: DeliveryRunDetail | null | undefined;
  /** Switches the host to the run's Overview tab ("View error" / "View run summary"). */
  onViewOverview?: (() => void) | undefined;
  /** Offer "Retry delivery" / "Run a new delivery" (same gate as the control rail). */
  showRetry?: boolean;
  /** Tighter padding for nested ledgers (work-unit drill-down, catalog panel). */
  compact?: boolean;
}

/**
 * Why a run's product ledger has no rows at all, as a state card: icon, short
 * title, one specific sentence, the facts behind it (dates, stage, error code)
 * and the next step. Replaces the old single grey sentence, which also misnamed
 * runs that failed before sending any product as "not tracked" / "aged out".
 */
export function ProductsUnavailableState({
  info,
  run,
  onViewOverview,
  showRetry = false,
  compact = false,
}: Props) {
  const { t } = useTranslation('deliveries');
  const style = REASON_STYLE[info.reason];
  const Icon = style.icon;
  const k = (leaf: string) => `detail.products.unavailable.${style.key}.${leaf}`;

  const facts: { label: string; value: string }[] = [];
  if (info.runStartedAt) {
    facts.push({
      label: t('detail.products.unavailable.facts.started'),
      value: formatDate(info.runStartedAt),
    });
  }
  if (info.expiredAt) {
    facts.push({
      label: t('detail.products.unavailable.facts.removed'),
      value: formatDate(info.expiredAt),
    });
  }

  let body: string;
  let hint: string | null = null;
  const actions: ReactNode[] = [];
  const retry = (label?: string) =>
    showRetry && run && canRetryDelivery(run) ? (
      <RetryDeliveryButton
        key="retry"
        run={run}
        presentation="card"
        {...(label ? { label } : {})}
      />
    ) : null;
  const overviewAction = (label: string) =>
    onViewOverview ? (
      <Button
        key="overview"
        variant="outline"
        size="sm"
        className="cursor-pointer"
        onClick={onViewOverview}
      >
        {label}
      </Button>
    ) : null;

  switch (info.reason) {
    case 'FAILED_BEFORE_PRODUCTS':
      body = info.lastSuccessfulStep
        ? t(k('bodyAfterStep'), { step: info.lastSuccessfulStep })
        : t(k('body'));
      actions.push(overviewAction(t(k('viewError'))), retry());
      break;
    case 'RETENTION_EXPIRED':
      body = t(k('body'), { days: info.retentionDays });
      hint = t(k('hint'));
      actions.push(overviewAction(t(k('viewSummary'))));
      break;
    case 'NOT_TRACKED': {
      const variant = run?.subVariant ?? null;
      const type = variant
        ? t(`detail.products.unavailable.deliveryType.${variant}`, {
            defaultValue: humanizeEnum(variant),
          })
        : null;
      body = type ? t(k('body'), { type }) : t(k('bodyNoType'));
      hint = t(k('hint'));
      actions.push(retry(t(k('runNew'))));
      break;
    }
    case 'ALL_SUPERSEDED':
      body = t(k('body'));
      actions.push(overviewAction(t(k('viewSummary'))));
      break;
  }

  const error =
    info.reason === 'FAILED_BEFORE_PRODUCTS' && info.errorCode
      ? describeDeliveryError(info.errorCode, info.errorMessage, t)
      : null;
  const visibleActions = actions.filter(Boolean);

  return (
    <Card
      role="status"
      data-reason={info.reason}
      className={cn(
        'flex flex-col gap-4 sm:flex-row sm:items-start',
        compact ? 'rounded-xl p-4' : 'p-6',
      )}
    >
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-2xl',
          compact ? 'size-10' : 'size-12',
          style.badge,
        )}
      >
        <Icon aria-hidden="true" className="size-5" />
      </span>
      <div className="min-w-0 flex-1 space-y-3">
        <div className="space-y-1">
          <p className="text-foreground text-base font-semibold">{t(k('title'))}</p>
          <p className="text-muted-foreground max-w-2xl text-sm leading-relaxed">{body}</p>
        </div>

        {error ? (
          <div className="flex flex-wrap items-start gap-2">
            <Badge
              variant="outline"
              className="border-error/30 bg-error-50 text-error pointer-events-none font-mono text-[11px]"
            >
              {error.code}
            </Badge>
            <span className="text-foreground min-w-0 text-sm break-words select-text">
              {error.detail ?? error.title}
            </span>
          </div>
        ) : null}

        {facts.length > 0 ? (
          <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
            {facts.map((fact) => (
              <div key={fact.label} className="flex gap-1.5">
                <dt className="text-muted-foreground">{fact.label}</dt>
                <dd className="text-foreground font-medium tabular-nums">{fact.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}

        {visibleActions.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">{visibleActions}</div>
        ) : null}
      </div>
    </Card>
  );
}
